import { fetchApiAllItems, type ResponseData, useApiQuery } from '../api';
import { useTeam } from '../hooks/team';
import { SESSION_STICKY_VIEW_QUERY_OPTIONS } from './viewQuery.utils';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { useMemo } from 'react';

export type Account = ResponseData<'get:/accounts'>['data'][number];

export const STORYBOOK_ACCOUNTS_QUERY_KEY = 'accounts';

export function useAccountsQuery(args?: {
  skip?: boolean;
  storybookQueryKey?: string;
  status?: activeStatus_enum;
}) {
  const [{ id: teamId }] = useTeam();

  const queryData = useApiQuery(
    async (api, signal) => {
      const fetchAccountsByStatus = async (status: 'active' | 'inactive') => {
        return await fetchApiAllItems(async (page) => {
          const result = await api.GET('/accounts', {
            params: {
              query: {
                limit: 200,
                page,
                status,
              },
            },
            signal,
          });

          if (result.error) {
            throw result.error;
          }

          return {
            items: result.data.data,
            hasNextPage: !!result.data.pagination.nextPage,
          };
        });
      };

      const [activeAccounts, inactiveAccounts] = await Promise.all([
        fetchAccountsByStatus('active'),
        fetchAccountsByStatus('inactive'),
      ]);

      return activeAccounts.concat(inactiveAccounts);
    },
    {
      queryGroup: 'accounts',
      uniqueQueryKey: 'accounts',
      variables: {
        teamId,
      },
      skip: args?.skip || !teamId,
      storybookQueryKey:
        args?.storybookQueryKey ?? STORYBOOK_ACCOUNTS_QUERY_KEY,
      ...SESSION_STICKY_VIEW_QUERY_OPTIONS,
    }
  );

  const data = useMemo(() => {
    if (!args?.status) return queryData.data;
    return queryData.data?.filter((account) => account.status === args?.status);
  }, [queryData.data, args?.status]);

  return {
    ...queryData,
    data,
  };
}
